import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

const Overlay = styled.div`
  position: fixed; inset: 0; width: 100vw; height: 100vh; background: rgba(17, 24, 39, 0.45);
  display: grid; place-items: center; z-index: 1200;
`;
const Card = styled.div`
  background: #fff; border: 1px solid #e5e7eb; border-radius: 12px;
  padding: 16px; width: min(92vw, 720px); max-width: 720px; box-shadow: 0 10px 20px rgba(0,0,0,0.08);
  max-height: 92vh; overflow: auto;
`;
const Title = styled.div`font-size: 16px; font-weight: 700; color: #111827;`;
const Actions = styled.div`margin-top: 14px; display: flex; justify-content: flex-end; gap: 10px;`;
const GhostButton = styled.button`
  height: 34px; padding: 0 12px; border-radius: 8px; border: 1px solid #e5e7eb;
  background: #fff; color: #374151; font-weight: 600; font-size: 13px; cursor: pointer;
`;
const Section = styled.div`margin-top: 12px;`;
const Label = styled.div`font-size: 12px; color: #6b7280; font-weight: 600; margin-bottom: 4px;`;
const Box = styled.div`
  border: 1px solid #e5e7eb; border-radius: 8px; padding: 10px; font-size: 13px;
  color: #111827; background: #ffffff;
`;
const Table = styled.div`
  border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; margin-top: 8px;
`;
const Row = styled.div`
  display: grid; grid-template-columns: 1.2fr 1fr 0.6fr; gap: 12px; align-items: center;
  padding: 10px 12px; border-top: 1px solid #f3f4f6;
  &:first-child { border-top: none; background: #fafafa; font-weight: 600; }
`;
const Pill = styled.span`
  display: inline-block; padding: 3px 8px; border-radius: 999px; font-size: 12px; font-weight: 700;
  color: #1f2937; background: #f3f4f6; border: 1px solid #e5e7eb;
`;

const PaginationBar = styled.div`
  display: flex; align-items: center; justify-content: space-between; margin-top: 10px; color: #6b7280; font-size: 12px;
`;
const PageControls = styled.div`display: flex; align-items: center; gap: 8px;`;
const SmallButton = styled.button`
  height: 28px; padding: 0 8px; border-radius: 6px; border: 1px solid #e5e7eb; background: #fff;
  color: #374151; font-weight: 600; font-size: 12px; cursor: pointer; &:disabled { opacity: 0.5; cursor: default; }
`;

function formatDateTime(isoString) {
  if (!isoString) return '—';
  const d = new Date(isoString);
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  }).format(d);
}

export default function ReportDeliveryViewModal({ isOpen, onClose, emailTemplates, reportSendDate, reports = [] }) {
  const subject = emailTemplates?.email_subject || '';
  const content = emailTemplates?.email_content || '';
  const rows = Array.isArray(reports) ? reports : [];
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const totalItems = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, currentPage]);

  if (!isOpen) return null;

  return createPortal(
    (
      <Overlay onClick={onClose}>
        <Card onClick={(e) => e.stopPropagation()}>
          <Title>Report Delivery</Title>

          <Section>
            <Label>Send date</Label>
            <Box>{formatDateTime(reportSendDate)}</Box>
          </Section>

          <Section>
            <Label>Email subject</Label>
            <Box>{subject || '—'}</Box>
          </Section>

          <Section>
            <Label>Email body</Label>
            <Box style={{ whiteSpace: 'pre-wrap' }}>{content || '—'}</Box>
          </Section>

          <Section>
            <Label>Recipients</Label>
            <Table role="table" aria-label="Report recipients">
              <Row>
                <div>Name</div>
                <div>Email</div>
                <div>Status</div>
              </Row>
              {pagedRows.map(r => (
                <Row key={r.id}>
                  <div>{r?.manager?.full_name || [r?.manager?.first_name, r?.manager?.last_name].filter(Boolean).join(' ') || '—'}</div>
                  <div>{r?.manager?.email || '—'}</div>
                  <div><Pill>{r?.status || '—'}</Pill></div>
                </Row>
              ))}
              {rows.length === 0 && (
                <Row>
                  <div style={{ gridColumn: '1 / -1', color: '#6b7280', fontSize: 13 }}>No recipients.</div>
                </Row>
              )}
            </Table>
            {rows.length > pageSize && (
              <PaginationBar>
                <div>
                  Showing {totalItems === 0 ? 0 : ((currentPage - 1) * pageSize + 1)}–{Math.min(currentPage * pageSize, totalItems)} of {totalItems}
                </div>
                <PageControls>
                  <SmallButton type="button" onClick={() => setPage(1)} disabled={currentPage === 1}>First</SmallButton>
                  <SmallButton type="button" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Prev</SmallButton>
                  <div style={{ fontSize: 12, color: '#374151' }}>Page {currentPage} / {totalPages}</div>
                  <SmallButton type="button" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next</SmallButton>
                  <SmallButton type="button" onClick={() => setPage(totalPages)} disabled={currentPage === totalPages}>Last</SmallButton>
                </PageControls>
              </PaginationBar>
            )}
          </Section>

          <Actions>
            <GhostButton type="button" onClick={onClose}>Close</GhostButton>
          </Actions>
        </Card>
      </Overlay>
    ),
    document.body
  );
}


